/* ==========================================================================
   DEVIANT — Custom subscription front end
   --------------------------------------------------------------------------
   WHY THIS WORKS WITH APPSTLE
   Appstle does not invent its own subscription mechanism. It creates native
   Shopify *selling plans*, which the theme can read through Liquid
   (variant.selling_plan_allocations) and post to the cart as `selling_plan`.
   So this file talks only to Shopify's own /cart/add.js. Appstle keeps doing
   all the back-end work: contracts, billing, renewals, the customer portal.
   Nothing here needs to change when Appstle updates its widget.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ADD_URL = ROOT.replace(/\/$/, '') + '/cart/add.js';

  /* ---------------------------------------------------------------- utils */

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
        return data;
      });
    });
  }

  function showError(scope, message) {
    var box = scope.querySelector('[data-dsub-error]');
    if (!box) { window.alert(message); return; }
    box.textContent = message;
    box.hidden = false;
    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function clearError(scope) {
    var box = scope.querySelector('[data-dsub-error]');
    if (box) box.hidden = true;
  }

  /* Add one line, then go where the section says to go.
     With a plan id it is a subscription line; without one (the gift page's
     one-time card) it is an ordinary purchase. `selling_plan` must be left
     out entirely in that case: sending 0 makes Shopify reject the line. */
  function addToCart(opts) {
    var item = {
      id: Number(opts.variantId),
      quantity: 1
    };
    if (opts.sellingPlanId) {
      item.selling_plan = Number(opts.sellingPlanId);
    }
    if (opts.properties && Object.keys(opts.properties).length) {
      item.properties = opts.properties;
    }
    return post(ADD_URL, { items: [item] }).then(function () {
      window.location.href = opts.redirect || ROOT + 'cart';
    });
  }

  /* ------------------------------------------------- side switch (tabs) */

  function initSwitch(section) {
    var buttons = section.querySelectorAll('[data-dsub-switch]');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = btn.getAttribute('data-dsub-switch');

        buttons.forEach(function (b) {
          b.setAttribute('aria-selected', String(b === btn));
        });
        section.querySelectorAll('[data-dsub-panel]').forEach(function (panel) {
          panel.hidden = panel.getAttribute('data-dsub-panel') !== target;
        });

        clearError(section);
      });
    });
  }

  /* --------------------------------------------------- "Start My Plan" */

  function initStartButtons(section) {
    section.querySelectorAll('[data-dsub-start]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('[data-dsub-card]') || section;
        clearError(section);
        card.classList.add('is-busy');

        addToCart({
          variantId: btn.getAttribute('data-variant'),
          sellingPlanId: btn.getAttribute('data-plan'),
          redirect: section.getAttribute('data-redirect')
        })['catch'](function (err) {
          card.classList.remove('is-busy');
          showError(section, err.message || 'Sorry, that could not be added. Please try again.');
        });
      });
    });
  }

  /* -------------------------------------------------- gift modal flow */

  function initGift(section) {
    var modal = document.querySelector('[data-dgift]');
    if (!modal) return;

    var form       = modal.querySelector('[data-dgift-form]');
    var planLabel  = modal.querySelector('[data-dgift-plan]');
    var lastFocus  = null;
    var current    = { variantId: null, sellingPlanId: null };

    function open(btn) {
      current.variantId = btn.getAttribute('data-variant');
      current.sellingPlanId = btn.getAttribute('data-plan');
      if (planLabel) planLabel.textContent = btn.getAttribute('data-plan-name') || '';

      lastFocus = btn;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';

      var first = modal.querySelector('input, textarea');
      if (first) first.focus();
    }

    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }

    section.querySelectorAll('[data-dsub-gift]').forEach(function (btn) {
      btn.addEventListener('click', function () { open(btn); });
    });

    modal.querySelectorAll('[data-dgift-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    modal.addEventListener('click', function (e) { if (e.target === modal) close(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    if (!form || form.dataset.bound) return;
    form.dataset.bound = 'true';

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var data = new FormData(form);
      var props = {};

      /* Underscore-prefixed keys stay hidden from the customer in cart and
         checkout; the rest are shown on the order, which is what we want
         for the gift details the fulfilment team needs to read. */
      props['Gift'] = 'Yes';
      if (data.get('recipient_name'))  props['Recipient name']  = data.get('recipient_name');
      if (data.get('recipient_email')) props['Recipient email'] = data.get('recipient_email');
      if (data.get('deliver_on'))      props['Send gift email on'] = data.get('deliver_on');
      if (data.get('message'))         props['Gift message'] = data.get('message');

      var submit = form.querySelector('[type="submit"]');
      if (submit) { submit.disabled = true; submit.dataset.label = submit.textContent; submit.textContent = 'Adding…'; }

      addToCart({
        variantId: current.variantId,
        sellingPlanId: current.sellingPlanId,
        properties: props,
        redirect: section.getAttribute('data-redirect')
      })['catch'](function (err) {
        if (submit) { submit.disabled = false; submit.textContent = submit.dataset.label || 'Add gift to cart'; }
        close();
        showError(section, err.message || 'Sorry, that gift could not be added. Please try again.');
      });
    });
  }

  /* ---------------------------------- product page radio picker sync */

  function initPicker(section) {
    var picker = section.querySelector('[data-dpick]');
    if (!picker) return;

    var radios = picker.querySelectorAll('input[name="selling_plan"]');
    var start  = section.querySelector('[data-dpick-start]');
    var gift   = section.querySelector('[data-dpick-gift]');

    function sync() {
      var checked = picker.querySelector('input[name="selling_plan"]:checked');
      if (!checked) return;
      [start, gift].forEach(function (btn) {
        if (!btn) return;
        btn.setAttribute('data-plan', checked.value);
        btn.setAttribute('data-variant', checked.getAttribute('data-variant'));
        btn.setAttribute('data-plan-name', checked.getAttribute('data-plan-name') || '');
      });
    }

    radios.forEach(function (r) { r.addEventListener('change', sync); });
    sync();
  }

  /* ------------------------------------------------------------- boot */

  function init() {
    document.querySelectorAll('[data-dsub]').forEach(function (section) {
      if (section.dataset.dsubReady) return;
      section.dataset.dsubReady = 'true';

      initSwitch(section);
      initPicker(section);
      initStartButtons(section);
      initGift(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-run inside the theme editor when a section is re-rendered. */
  document.addEventListener('shopify:section:load', init);
})();
